import { lazy, Suspense } from "react";
import { Outlet, Navigate } from "react-router-dom";
import AccountingFranchiseBoundary from '../components/global/Auth/AccountingFranchiseBoundary';
import MainLayout from "../components/global/Layout/MainLayout";

import { ERouteKey } from "../compiler/route.enum";
import CircularProgress from "@mui/material/CircularProgress";

// protected route
import RequireAuth from "./RequreAuth";
import NoAuth from "./NoAuth/NoAuth";
import { IRoutes } from "./route.interface";

//icons
import PersonPinTwoToneIcon from "@mui/icons-material/PersonPinTwoTone";
import PersonPinCircleTwoToneIcon from "@mui/icons-material/PersonPinCircleTwoTone";
import PointOfSaleTwoToneIcon from "@mui/icons-material/PointOfSaleTwoTone";
import DescriptionTwoToneIcon from "@mui/icons-material/DescriptionTwoTone";
import LocalShippingTwoToneIcon from "@mui/icons-material/LocalShippingTwoTone";
import StorefrontTwoToneIcon from "@mui/icons-material/StorefrontTwoTone";
import TodayTwoToneIcon from "@mui/icons-material/TodayTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import VerifiedUserTwoToneIcon from "@mui/icons-material/VerifiedUserTwoTone";
import LoyaltyTwoToneIcon from "@mui/icons-material/LoyaltyTwoTone";
import BookmarkTwoToneIcon from "@mui/icons-material/BookmarkTwoTone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ShoppingBasketTwoToneIcon from "@mui/icons-material/ShoppingBasketTwoTone";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LanguageIcon from "@mui/icons-material/Language";
import CategoryIcon from "@mui/icons-material/Category";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import DeliveryStatusEntry from "../pages/Delivery/DeliveryStatusEntry";
import SettingAccessUserPage from "../pages/Setting/Access/User";
import SettingAccessUserGroupPage from "../pages/Setting/Access/UserGroup";
import BillingPage from "../pages/Billing/BillingPage";
import BillingWorkspace from "../pages/Billing/BillingWorkspace";
import BillingInvoiceListPage from "../pages/Billing/BillingInvoiceListPage";
import BillingCompanyInvoiceListPage from "../pages/Billing/BillingCompanyInvoiceListPage";
import CustomerBillingPage from "../pages/CustomerOrderPortal/CustomerBillingPage";
import CustomerBillingInvoiceListPage from "../pages/CustomerOrderPortal/CustomerBillingInvoiceListPage";

// Super Admin Pages
const SuperAdminDashboard = lazy(() => import("../pages/SuperAdmin/"));
const SuperAdminPasswordGuard = lazy(
  () => import("../components/global/Guards/SuperAdminPasswordGuard")
);

// page, layout
const NotFoundPage = lazy(() => import("../pages/NotFound"));
const LoginPage = lazy(() => import("../pages/Login"));
const MilkrunnBotLinkPage = lazy(() => import("../pages/MilkrunnBotLink"));
const PrivacyPolicyPage = lazy(() => import("../pages/Privacy"));
const DashBoardPage = lazy(() => import("../pages/Dashboard"));
const SettingLayout = lazy(() => import("../components/global/Layout/SettingLayout"));

const SiteMap = lazy(() => import("../pages/SiteMap"));
const FarmBookLoginPage = lazy(() => import("../pages/FarmBookLogin"));

//setting
const ProductCategoriesSetup = lazy(() => import("../pages/Setting/ProductCategories/Setup"));
const ProductCategoriesShortcuts = lazy(
  () => import("../pages/Setting/ProductCategories/Shortcuts")
);

const WelcomePage = lazy(() => import("../pages/Dashboard/Welcome"));
const NoPermissionPage = lazy(() => import("../pages/NoPermission"));
const Register = lazy(() => import("../pages/Members/Register"));
const Subscribe = lazy(() => import("../pages/Members/Subscribe"));
const Buyer = lazy(() => import("../pages/Members/Buyer"));
const Seller = lazy(() => import("../pages/Members/Seller"));
const NewsLetter = lazy(() => import("../pages/Members/NewsLetter"));
const BuySell = lazy(() => import("../pages/Finance/BuySell"));
const FinanceInfo = lazy(() => import("../pages/Finance/FinanceInfo"));
const Complaints = lazy(() => import("../pages/HelpdeskSupport/Complaints"));
const HelpdeskSetting = lazy(() => import("../pages/HelpdeskSupport/HelpdeskSetting"));
const DeliveryReport = lazy(() => import("../pages/Reports/DeliveryReport"));
const DeliveryRoundReport = lazy(() => import("../pages/Reports/DeliveryRoundReport"));
const PunthaiReport = lazy(() => import("../pages/Reports/PunthaiReport"));
const PunthaiDetailReport = lazy(() => import("../pages/Reports/PunthaiDetailReport"));
const FactoryReport = lazy(() => import("../pages/Reports/FactoryReport"));
const FactoryDetailReport = lazy(() => import("../pages/Reports/FactoryDetailReport"));
const BuyerReport = lazy(() => import("../pages/Reports/BuyerReport"));
const StoreReport = lazy(() => import("../pages/Reports/StoreReport"));
const ProductReport = lazy(() => import("../pages/Reports/ProductReport"));
const ProductList = lazy(() => import("../pages/Product/ProductList"));

// TODO: Uncomment these imports when WareHouse and Purchasing modules are fixed
// const WareHouseInformationPage = lazy(() => import("../pages/WareHouse/Information"));
// const WareHouseOrdersPage = lazy(() => import("../pages/WareHouse/Orders"));
// const WareHouseOrderDetail = lazy(() => import("../pages/WareHouse/Orders/OrderDetail"));
// const WareHouseOrderDetailProductDetail = lazy(
//   () => import("../pages/WareHouse/Orders/OrderDetail/ProductDetail")
// );
// const WareHouseInformationCreatePage = lazy(() => import("../pages/WareHouse/Information/CRUD"));
// const PurchasingProductionOrderDetailPage = lazy(
//   () => import("../pages/Purchasing/ProductionOrder/Detail")
// );
const PurchasingItemPage = lazy(() => import("../pages/Purchasing/Item"));
const PurchasingItemDetailPage = lazy(() => import("../pages/Purchasing/Item/Detail"));
// const PurchasingProductionOrderPage = lazy(() => import("../pages/Purchasing/ProductionOrder"));
const PurchasingPurchaseOrderPage = lazy(() => import("../pages/Purchasing/PurchaseOrder"));
// const PurchasingPurchaseOrderDetailPage = lazy(
//   () => import("../pages/Purchasing/PurchaseOrder/Detail")
// );

const ProductListLine = lazy(() => import("../pages/Product/ProductListLine"));
const ProductListLineCreate = lazy(() => import("../pages/Product/ProductListLine/Create"));
const ProductListLineEdit = lazy(() => import("../pages/Product/ProductListLine/Edit"));
const PackagingManagementPage = lazy(() => import("../pages/PackagingManagement"));
const BannerHomepageList = lazy(() => import("../pages/WebData/BannerHomepageList"));
const BannerHomepageListCreate = lazy(() => import("../pages/WebData/BannerHomepageListCreate"));
const BannerHomepageListEdit = lazy(() => import("../pages/WebData/BannerHomepageListEdit"));
const BannerGreetingModalList = lazy(() => import("../pages/WebData/BannerGreetingModalList"));
const BannerGreetingModalListCreate = lazy(
  () => import("../pages/WebData/BannerGreetingModalListCreate")
);
const BannerGreetingModalListEdit = lazy(
  () => import("../pages/WebData/BannerGreetingModalListEdit")
);

// User Branch Management
const UserBranchManagementPage = lazy(() => import("../pages/UserBranch"));
const LineShopOrders = lazy(() => import("../pages/Orders/LineShop"));
const SettingBusinessPage = lazy(() => import("../pages/Setting/Business"));
const BroadcastAiSettingPage = lazy(() => import("../pages/Setting/AI"));
const EpodGeofenceSettingPage = lazy(() => import("../pages/Setting/EpodGeofence"));
const DeliveryIntegrationSettingsPage = lazy(() => import("../pages/Setting/DeliveryIntegration"));
const SessionManagementPage = lazy(() => import("../pages/Setting/SessionManagement"));
const ShippingProviders = lazy(() => import("../pages/Setting/Shipping"));
const EnvironmentBadgeDemoPage = lazy(() => import("../pages/Setting/EnvironmentBadgeDemo"));
const BroadcastPage = lazy(() => import("../pages/Broadcast"));
const BroadcastLayout = lazy(() => import("../pages/Broadcast/BroadcastLayout"));
const OrderReminderPage = lazy(() => import("../pages/OrderReminder"));

// Franchise Management
const FranchisePage = lazy(() => import("../pages/Franchise"));

// Promotion Management
const PromotionsPage = lazy(() => import("../pages/Promotions/PromotionsPage"));

// Customer Drop Management
const CustomerDropManagementPage = lazy(() => import("../pages/CustomerDrop"));

// Excel Route Management
const ExcelRouteManagementPage = lazy(() =>
  import("../pages/ExcelRoute/ExcelRouteManagementPage").then((module) => ({
    default: module.ExcelRouteManagementPage,
  }))
);
const ExcelRouteExportOrdersPage = lazy(() =>
  import("../pages/ExcelRoute/ExcelRouteManagementPage").then((module) => ({
    default: module.ExcelRouteExportOrdersPage,
  }))
);
const ExcelRouteImportRoutesPage = lazy(() =>
  import("../pages/ExcelRoute/ExcelRouteManagementPage").then((module) => ({
    default: module.ExcelRouteImportRoutesPage,
  }))
);
const OsrmControlSheetImportPage = lazy(() =>
  import("../pages/ExcelRoute/OsrmControlSheetImportPage").then((module) => ({
    default: module.OsrmControlSheetImportPage,
  }))
);

// Customer Management
const CustomerManagementPage = lazy(() => import("../pages/CustomerManagement"));

// Billing Cycle Template
const BillingCycleTemplatePage = lazy(
  () => import("../pages/Setting/BillingCycleTemplate/BillingCycleTemplatePage")
);

// Delivery Setup
const DeliverySetupPage = lazy(() => import("../pages/DeliverySetup"));

// Delivery Route Planning (New)
const DeliveryRoutePlanningPage = lazy(
  () => import("../pages/DeliveryRoutePlanning/DeliveryRoutePlanning")
);
const TransportRoutePlanningPilotPage = lazy(() => import("../pages/TransportRoutePlanningPilot"));
const OsrmRouteSyncReviewPage = lazy(() => import("../pages/OsrmRouteSyncReview"));

// Delivery Route Info (New)
const DeliveryRouteInfoPage = lazy(() => import("../pages/DeliveryRouteInfo"));
const DriverManualPage = lazy(() => import("../pages/DriverManual"));
const DriverManagementPage = lazy(() => import("../pages/DriverManagement"));

// Sales Orders Pages
const SalesOrdersList = lazy(() => import("../pages/SalesOrders/SalesOrdersList"));
const SalesOrdersDetail = lazy(() => import("../pages/SalesOrders/SalesOrdersDetail"));

// Sales Orders (Update) Pages
const SalesOrdersListUpdate = lazy(() => import("../pages/SalesOrdersUpdate"));

// Under Construction Page for unfinished features
const UnderConstruction = lazy(() => import("../pages/UnderConstruction"));
const WarehouseManagementPage = lazy(() => import("../pages/WareHouse/WarehouseManagementPage"));

// Order Branch
const OrdersBranch = lazy(() => import("../pages/OrdersBranch/OrdersBranch"));

// Invoice Pages
const InvoiceList = lazy(() => import("../pages/Invoice/InvoiceList"));
const InvoiceDetail = lazy(() => import("../pages/Invoice/InvoiceDetailPage"));
const InvoiceEmailManagementPage = lazy(() => import("../pages/InvoiceEmailManagementPage"));

// Netbay SFTP Upload Page
const NetbaySftpUploadPage = lazy(() => import("../pages/NetbaySftp/NetbaySftpUploadPage"));

// Netbay PDF → Google Drive Upload Page
const NetbayPdfUploaderPage = lazy(() => import("../pages/Invoice/NetbayPdfUploaderPage"));

// Billing PDF Generator Page
const BillingPdfGeneratorPage = lazy(() => import("../pages/Invoice/BillingPdfGeneratorPage"));

// Line Chat History Page
const LineChatHistoryPage = lazy(() => import("../pages/LineChat/LineChatHistoryPage"));
const LineTransportManagementPage = lazy(
  () => import("../pages/LineTransport/LineTransportManagementPage")
);
const LineTransportChatArchivePage = lazy(
  () => import("../pages/LineTransport/LineTransportChatArchivePage")
);
const EpodAppOperationsPage = lazy(() => import("../pages/EpodAppOperations"));
const RequestCenterPage = lazy(() => import('../pages/RequestCenter'));
const SmsOtpLogsPage = lazy(() => import("../pages/SmsOtpLogs"));

const routePlanningPilotEnabled =
  import.meta.env.VITE_ROUTE_PLANNING_PILOT === "true" || import.meta.env.VITE_IS_DEV === "true";

// Claim Management Page
const ClaimManagementPage = lazy(() => import("../pages/ClaimManagement"));

// Customer Order Portal Pages

export const routes: IRoutes[] = [
  {
    key: ERouteKey.DELIVERY,
    name: "Welcome",
    path: "",
    element: <Navigate to="/welcome" replace />,
    code: "",
  },
  {
    key: ERouteKey.OUTLET,
    name: "เมนูทั้งหมด",
    path: "",
    element: (
      <RequireAuth>
        <MainLayout>
          <Suspense
            fallback={
              <div
                style={{
                  display: "grid",
                  width: "100%",
                  height: "100%",
                  placeItems: "center",
                }}
              >
                <CircularProgress color="primary" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </MainLayout>
      </RequireAuth>
    ),
    children: [
      {
        // code: "helpdesk_requests",
        key: "REQUEST_CENTER",
        name: "Helpdesk · คำร้อง",
        path: "request-center",
        icon: <DescriptionTwoToneIcon />,
        element: <RequestCenterPage />,
      },
      // TODO: New menu structure based on MILKRUNN admin diagram

      // 0. Welcome Page (Hidden)
      {
        code: "00",
        isPrivateLink: true,
        key: ERouteKey.WELCOME,
        name: "Welcome",
        path: "welcome",
        icon: <DashboardOutlinedIcon />,
        element: <WelcomePage />,
      },

      // 1. รายงาน (Reports)
      {
        code: "01",
        key: ERouteKey.REPORT_PUNTHAI,
        name: "รายงาน",
        path: "reports",
        icon: <DescriptionTwoToneIcon />,
        element: <Outlet />,
        children: [
          {
            code: "01-01",
            key: ERouteKey.REPORT_BUYER,
            name: "ซื้อ",
            path: "reports-buy",
            isPrivateLink: true, // Temporarily hidden: not in active use.
            element: (
              <UnderConstruction
                title="รายงานการซื้อ"
                description="ระบบรายงานการซื้อกำลังอยู่ในระหว่างการพัฒนา"
              />
            ),
          },
          {
            code: "01-02",
            key: ERouteKey.REPORT_BUYER,
            name: "ขาย",
            path: "reports-sell",
            isPrivateLink: true, // Temporarily hidden: not in active use.
            element: (
              <UnderConstruction
                title="รายงานการขาย"
                description="ระบบรายงานการขายกำลังอยู่ในระหว่างการพัฒนา"
              />
            ),
          },
          {
            code: "01-03",
            key: ERouteKey.REPORT_BUYER,
            name: "ส่ง",
            path: "reports-deliver",
            isPrivateLink: true, // Temporarily hidden: not in active use.
            element: <DeliveryReport />,
          },
          {
            code: "01-04",
            key: ERouteKey.REPORT_PUNTHAI,
            name: "รายงานรอบวันส่ง", // สำหรับพันธุ์ไทย
            path: "delivery-report",
            isPrivateLink: true, // Temporarily hidden: not in active use.
            element: <DeliveryRoundReport />, // ใช้ component ใหม่
          },
          {
            code: "01-05",
            key: ERouteKey.REPORT_PUNTHAI,
            name: "รายงานใบสั่งผลิด", // สำหรับโรงงาน
            path: "factory-report",
            element: <FactoryReport />,
          },
          // {
          //   key: ERouteKey.REPORT_PUNTHAI,
          //   name: "รายงานใบกำกับภาษี",
          //   path: "invoice-report",
          //   element: <InvoiceReport />,
          // }
          // {
          //   // code: "06-01",
          //   key: ERouteKey.REPORT_PUNTHAI,
          //   name: "รายงานพันธุ์ไทย", // สำหรับพันธุ์ไทย
          //   path: "punthai-report",
          //   element: <PunthaiReport />, // รอทำหน้าใหม่ (เก็บของเดิมไว้)
          // },
          // {
          // code: "01-05",
          //   isPrivateLink: true,
          //   key: ERouteKey.REPORT_PUNTHAI_DETAIL,
          //   name: "รายงานพันธุ์ไทยรายละเอียด",
          //   path: "punthai-detail-report",
          //   element: <PunthaiDetailReport />,
          // },

          // {
          //   isPrivateLink: true,
          //   key: ERouteKey.REPORT_PUNTHAI,
          //   name: "รายงานใบสั่งผลิดรายละเอียด",
          //   path: "factory-detail-report",
          //   element: <FactoryDetailReport />,
          // },
          // {
          //   key: ERouteKey.REPORT_BUYER,
          //   name: "รายงานใบสั่งงาน", // สำหรับขนส่ง
          //   path: "workorder-report",
          //   element: <PunthaiReport />,
          // },
          // {
          //   key: ERouteKey.REPORT_STORE,
          //   name: "รายงานวิเคราะห์", // สำหรับฝ่ายบริหาร
          //   path: "management-report",
          //   element: <DeliveryReport />, // รอสร้างหน้าจริง
          // },
        ],
      },

      // แผนการจัดซื้อ สำหรับ Mockup
      // {
      //     // code: "03",
      //     key: ERouteKey.PURCHASE_OUTLET,
      //     name: "แผนการจัดซื้อ",
      //     path: "purchasing",
      //     icon: <TodayTwoToneIcon />,
      //     element: <Outlet />,
      //     children: [
      //         {
      //             // code: "03-01",
      //             key: ERouteKey.PURCHASE_LIST_OUTLET,
      //             name: "รายสินค้า",
      //             path: "item",
      //             element: <Outlet />,
      //             children: [
      //                 {
      //                     key: ERouteKey.PURCHASE_LIST,
      //                     path: "",
      //                     element: <PurchasingItemPage />,
      //                 },
      //                 {
      //                     name: "ข้อมูลรายการสินค้า",
      //                     key: ERouteKey.PURCHASE_LIST_DETAIL_OUTLET,
      //                     path: "detail",
      //                     element: <Outlet />,
      //                     children: [
      //                         {
      //                             key: ERouteKey.PURCHASE_LIST_DETAIL_OUTLET_NAVIGATE,
      //                             path: "",
      //                             element: <Navigate to={"/purchasing/item"} />,
      //                         },
      //                         {
      //                             isPrivateLink: true,
      //                             name: "รายละเอียด",
      //                             key: ERouteKey.PURCHASE_LIST_DETAIL_ID,
      //                             path: ":id",
      //                             element: <PurchasingItemDetailPage />,
      //                         },
      //                     ],
      //                 },
      //             ],
      //         },
      //         // {
      //         //     // code: "03-02",
      //         //     key: ERouteKey.PURCHASE_ORDERS_OUTLET,
      //         //     name: "ใบสั่งผลิต",
      //         //     path: "productionOrder",
      //         //     element: <Outlet />,
      //         //     children: [
      //         //         {
      //         //             key: ERouteKey.PURCHASE_ORDERS,
      //         //             path: "",
      //         //             element: <PurchasingProductionOrderPage />,
      //         //         },
      //         //         {
      //         //             isPrivateLink: true,
      //         //             key: ERouteKey.PURCHASE_ORDERS_COPY,
      //         //             name: "ใบสั่งผลิต",
      //         //             path: "copy",
      //         //             // element: <CopyPurchasingProductionOrderDetailPage />,
      //         //             element: <PurchasingProductionOrderDetailPage />,
      //         //         },
      //         //         {
      //         //             name: "ข้อมูลใบสั่งผลิต",
      //         //             key: ERouteKey.PURCHASE_ORDERS_DETAIL_OUTLET,
      //         //             path: "detail",
      //         //             element: <Outlet />,
      //         //             children: [
      //         //                 {
      //         //                     key: ERouteKey.PURCHASE_ORDERS_DETAIL_OUTLET_NAVIGATE,
      //         //                     path: "",
      //         //                     element: <PurchasingProductionOrderDetailPage />,
      //         //                 },
      //         //                 {
      //         //                     isPrivateLink: true,
      //         //                     key: ERouteKey.PURCHASE_LIST_DETAIL_ID,
      //         //                     path: ":id",
      //         //                     element: <PurchasingProductionOrderDetailPage />,
      //         //                 },
      //         //             ],
      //         //         },
      //         //     ],
      //         // },
      //         // {
      //         //     // code: "03-03",
      //         //     key: ERouteKey.PURCHASE_BUYING_OUTLET,
      //         //     name: "ใบสั่งซื้อ",
      //         //     path: "purchaseOrder",
      //         //     element: <Outlet />,
      //         //     children: [
      //         //         {
      //         //             key: ERouteKey.PURCHASE_BUYING,
      //         //             path: "",
      //         //             element: <PurchasingPurchaseOrderPage />,
      //         //         },
      //         //         {
      //         //             name: "ข้อมูลใบสั่งผลิต",
      //         //             key: ERouteKey.PURCHASE_BUYING_DETAIL_OUTLET,
      //         //             path: "detail",
      //         //             element: <Outlet />,
      //         //             children: [
      //         //                 {
      //         //                     key: ERouteKey.PURCHASE_BUYING_OUTLET_NAVIGATE,
      //         //                     path: "",
      //         //                     element: <PurchasingPurchaseOrderDetailPage />,
      //         //                 },
      //         //                 {
      //         //                     isPrivateLink: true,
      //         //                     key: ERouteKey.PURCHASE_BUYING_DETAIL_ID,
      //         //                     path: ":id",
      //         //                     element: <PurchasingPurchaseOrderDetailPage />,
      //         //                 },
      //         //             ],
      //         //         },
      //         //     ],
      //         // },
      //     ],
      // },

      // 2. รายการขาย (Sales Orders)
      {
        code: "02",
        key: ERouteKey.PRODUCT_SALES,
        name: "รายการขาย",
        path: "sales",
        icon: <PointOfSaleTwoToneIcon />,
        element: <AccountingFranchiseBoundary />,
        children: [
          // {
          //   code: "02-01",
          //   key: ERouteKey.ORDERS_LINE,
          //   name: "ดูรายการขาย (เก่า)",
          //   path: "sales-list",
          //   element: <SalesOrdersList />,
          // },
          {
            code: "02-02",
            key: ERouteKey.ORDERS_LINE,
            name: "รายการคืนสินค้า",
            path: "sales-return",
            isPrivateLink: true, // Temporarily hidden from the sidebar.
            element: (
              <UnderConstruction
                title="รายการคืนสินค้า"
                description="ระบบจัดการการคืนสินค้ากำลังอยู่ในระหว่างการพัฒนา"
              />
            ),
          },
          {
            code: "02-03",
            key: ERouteKey.ORDERS_LINE,
            name: "ดูรายการขาย",
            path: "sales-list-update",
            element: <SalesOrdersListUpdate />,
          },
          {
            isPrivateLink: true,
            key: ERouteKey.ORDERS_LINE,
            name: "รายละเอียดการขาย",
            path: "sales-detail",
            element: <SalesOrdersDetail />,
          },
          {
            code: "02-04",
            key: ERouteKey.INVOICE,
            name: "ใบกำกับภาษี/ใบแจ้งหนี้",
            path: "invoice-list",
            element: <InvoiceList />,
          },
          {
            isPrivateLink: true,
            key: ERouteKey.INVOICE,
            name: "รายละเอียดใบกำกับภาษี",
            path: "invoice-detail",
            code: "02-04",
            element: <InvoiceDetail />,
          },
          {
            code: "02-05",
            key: ERouteKey.INVOICE,
            name: "📧 จัดการการส่งอีเมล",
            path: "invoice-email-management",
            element: <InvoiceEmailManagementPage />,
          },
          {
            code: "02-06",
            key: ERouteKey.INVOICE,
            name: "วางบิลและเอกสาร",
            path: "billing",
            element: <BillingWorkspace><BillingPage /></BillingWorkspace>,
          },
          {
            isPrivateLink: true,
            key: ERouteKey.INVOICE,
            code: "02-06",
            name: "รายการใบวางบิล",
            path: "billing-invoices",
            element: <BillingWorkspace><BillingInvoiceListPage /></BillingWorkspace>,
          },
          {
            isPrivateLink: true,
            key: ERouteKey.INVOICE,
            code: "02-06",
            name: "รายการบิลลูกค้า",
            path: "billing-company-invoices",
            element: <BillingWorkspace><BillingCompanyInvoiceListPage /></BillingWorkspace>,
          },
          // {
          //   code: "02-07",
          //   key: ERouteKey.LINE_CHAT_HISTORY,
          //   name: "💬 ประวัติ Line Chat",
          //   path: "line-chat-history",
          //   element: <LineChatHistoryPage />,
          // },
          {
            code: "02-08",
            key: ERouteKey.NETBAY_SFTP_UPLOAD,
            name: "ส่งเอกสาร SFTP",
            path: "netbay-sftp-upload",
            isPrivateLink: true, // Temporarily hidden from the sidebar.
            element: <NetbaySftpUploadPage />,
          },

          {
            code: "02-09",
            key: ERouteKey.CLAIM_MANAGEMENT,
            name: "จัดการเคลม",
            path: "claim-management",
            element: <ClaimManagementPage />,
          },
          {
            code: "02-10",
            key: ERouteKey.NETBAY_PDF_UPLOAD,
            name: "📤 สร้าง PDF สรรพากร",
            path: "netbay-pdf-upload",
            element: <NetbayPdfUploaderPage />,
          },
          {
            code: "02-11",
            key: ERouteKey.BILLING_PDF_GENERATOR,
            name: "📄 สร้าง PDF วางบิล",
            path: "billing-pdf-generator",
            element: <BillingPdfGeneratorPage />,
          },
          {
            isPrivateLink: true,
            code: "02-11",
            key: ERouteKey.BILLING_PDF_GENERATOR,
            name: "PDF วางบิลจากรอบบิล",
            path: "billing-center/pdf",
            element: <BillingWorkspace><BillingPdfGeneratorPage key="billing" workflow="billing" /></BillingWorkspace>,
          },
          {
            isPrivateLink: true,
            code: "02-05",
            key: ERouteKey.INVOICE,
            name: "ส่งเอกสารจากรอบบิล",
            path: "billing-center/email",
            element: <BillingWorkspace><InvoiceEmailManagementPage key="billing" workflow="billing" /></BillingWorkspace>,
          },
        ],
      },

      // 7. Customer Order Portal (สำหรับลูกค้า: เจ้าของแฟรนไชส์, ผู้จัดการสาขา)
      {
        code: "07",
        key: ERouteKey.CUSTOMER_PORTAL_OUTLET,
        name: "มุมมองลูกค้า",
        path: "customer-portal",
        icon: <DescriptionTwoToneIcon />,
        element: <Outlet />,
        children: [
          {
            code: "07-02",
            key: ERouteKey.CUSTOMER_PORTAL_INVOICE_LIST,
            name: "รายการรอบบิล",
            path: "invoice-list",
            element: <CustomerBillingPage />,
          },
          {
            code: "07-02",
            isPrivateLink: true,
            key: ERouteKey.CUSTOMER_PORTAL_BILLING_INVOICES,
            name: "รายการบิลลูกค้า",
            path: "invoice-list/billing-invoices",
            element: <CustomerBillingInvoiceListPage />,
          },
        ],
      },

      // 3. รายการส่ง (Delivery)
      {
        code: "03",
        key: ERouteKey.DELIVERY,
        name: "รายการส่ง",
        path: "delivery",
        icon: <LocalShippingTwoToneIcon />,
        element: <Outlet />,
        children: [
          // {
          //   code: "03-01",
          //   key: ERouteKey.DELIVERY,
          //   name: "ดูรายการส่ง",
          //   path: "delivery-list",
          //   element: <PurchasingItemDetailPage />,
          // },
          // {
          //   code: "03-02",
          //   key: ERouteKey.DELIVERY,
          //   name: "จัดสายส่ง",
          //   path: "delivery-setup",
          //   element: <DeliverySetupPage />,
          // },
          // { // TODO: ทำหน้ารายการออเดอร์ สำหรับ Export เพื่อนำข้อมูลไปใช้จัดสายส่ง
          //   key: ERouteKey.DELIVERY_ORDERS_OUTLET,
          //   name: "รายการส่งสินค้า",
          //   path: "delivery-orders",
          //   element: <Outlet />,
          // },

          {
            // TODO: Export ข้อมูลรายการออเดอร์ สำหรับจัดสายส่ง, Validate ข้อมูลสายส่ง, Import ข้อมูลสายส่งที่จัดเสร็จแล้ว
            // code: "03-02-1",
            key: ERouteKey.EXCEL_ROUTE_MANAGEMENT,
            name: "จัดการข้อมูลสายส่ง",
            path: "excel-route-management",
            isPrivateLink: true,
            element: <ExcelRouteManagementPage />,
          },
          {
            code: "03-01",
            key: ERouteKey.EXCEL_ROUTE_EXPORT_ORDERS,
            name: "Export ออเดอร์",
            path: "export-orders",
            element: <ExcelRouteExportOrdersPage />,
          },
          {
            code: "03-02",
            key: ERouteKey.EXCEL_ROUTE_IMPORT_ROUTES,
            name: "Import สายส่ง",
            path: "import-routes",
            element: <ExcelRouteImportRoutesPage />,
          },
          // {
          //   // Reuse the existing 03-02 permission until a separate ACL resource is provisioned.
          //   code: "03-02",
          //   key: ERouteKey.OSRM_CONTROL_SHEET_IMPORT,
          //   name: "Import Control Sheet",
          //   sidebarName: "Import Control Sheet",
          //   path: "import-osrm-control-sheet",
          //   element: <OsrmControlSheetImportPage />,
          // },
          // {
          //   // Reuse Route Data permission so existing transport planners do not need a new ACL.
          //   code: "03-06",
          //   key: ERouteKey.TRANSPORT_ROUTE_PLANNING_PILOT,
          //   name: "จัดสายส่ง",
          //   sidebarName: "จัดสายส่ง",
          //   path: "route-planning",
          //   icon: <AltRouteIcon />,
          //   element: <TransportRoutePlanningPilotPage />,
          // },
          {
            code: "90",
            key: ERouteKey.OSRM_ROUTE_SYNC_REVIEW_LEGACY_REDIRECT,
            isPrivateLink: true,
            path: "osrm-route-sync-review",
            element: <Navigate to="/setting/osrm-route-sync-review" replace />,
          },

          // {
          //   // code: "03-02-1",
          //   key: ERouteKey.DELIVERY_ROUTE_PLANNING,
          //   name: "นำเข้าสายส่ง (ใหม่)",
          //   path: "delivery-route-planning",
          //   element: <DeliveryRoutePlanningPage />,
          // },

          // {
          //   // code: "03-02-1",
          //   key: ERouteKey.DELIVERY_ROUTE_PLANNING,
          //   name: "นำเข้าสายส่ง",
          //   path: "delivery-route-planning",
          //   element: <DeliveryRoutePlanningPage />,
          // },

          {
            code: "90",
            key: ERouteKey.LINE_TRANSPORT_MANAGEMENT_LEGACY_REDIRECT,
            isPrivateLink: true,
            path: "delivery-transport",
            element: <Navigate to="/setting/delivery-transport" replace />,
          },
          {
            code: "03-05",
            key: ERouteKey.LINE_TRANSPORT_CHAT_ARCHIVE,
            name: "ประวัติแชทขนส่ง LINE",
            path: "line-transport-chat-archive",
            element: <LineTransportChatArchivePage />,
          },
          {
            code: "03-04",
            key: ERouteKey.DELIVERY,
            name: "สถานะขนส่ง",
            path: "delivery-status",
            element: <DeliveryStatusEntry />,
          },
          {
            code: "03-06",
            key: ERouteKey.DELIVERY_ROUTE_PLANNING,
            name: "ข้อมูลสายส่ง",
            path: "delivery-route-info",
            element: <DeliveryRouteInfoPage />,
          },
          {
            code: "03-07",
            isPrivateLink: true,
            key: ERouteKey.EPOD_APP_OPERATIONS_LEGACY_REDIRECT,
            path: "epod-app-operations",
            element: <Navigate to="/setting/app-operations/epod" replace />,
          },
          {
            code: "03-08",
            key: ERouteKey.DRIVER_MANAGEMENT,
            name: "จัดการคนขับ",
            path: "driver-management",
            element: <DriverManagementPage />,
          },
          {
            key: ERouteKey.DRIVER_MANUAL,
            name: "คู่มือคนขับ",
            path: "driver-manual",
            element: <DriverManualPage />,
          },
        ],
      },

      // 4. SKU (Product Management)
      {
        code: "04",
        key: ERouteKey.PRODUCT_OUTLET,
        name: "SKU",
        path: "sku",
        icon: <LoyaltyTwoToneIcon />,
        element: <Outlet />,
        children: [
          {
            code: "04-01",
            key: ERouteKey.PRODUCT_LIST_LINE,
            name: "รายการ SKU",
            path: "sku-list",
            element: <Outlet />,
            children: [
              {
                key: ERouteKey.PRODUCT_LIST,
                path: "",
                element: <ProductListLine />,
              },
              {
                isPrivateLink: true,
                key: ERouteKey.PRODUCT_LIST_LINE_CREATE,
                name: "เพิ่มสินค้าใหม่",
                path: "create",
                element: <ProductListLineCreate />,
              },
              {
                isPrivateLink: true,
                key: ERouteKey.PRODUCT_LIST_LINE_EDIT,
                name: "แก้ไขสินค้า",
                path: "edit/:uuid",
                element: <ProductListLineEdit />,
              },
            ],
          },
          {
            // code: "04-05",
            key: ERouteKey.PACKAGING_MANAGEMENT,
            name: "จัดการตะกร้า",
            path: "package-management",
            element: <PackagingManagementPage />,
          },
          {
            code: "04-02",
            key: ERouteKey.SETTING_PRODUCT_CATEGORY,
            name: "หมวดหมู่ SKU",
            path: "sku-categories",
            element: <ProductCategoriesSetup />,
          },
          {
            code: "04-03",
            key: "PRODUCT_WAREHOUSE",
            name: "คลัง SKU",
            path: "sku-warehouse",
            element: <WarehouseManagementPage />,
          },
          {
            code: "04-04",
            key: "PRODUCT_TRANSFER",
            name: "รายการโอน SKU",
            path: "sku-transfer",
            element: (
              <UnderConstruction
                title="รายการโอน SKU"
                description="ระบบโอน SKU กำลังอยู่ในระหว่างการพัฒนา"
              />
            ),
          },
        ],
      },

      // 5. ลูกค้า/ผู้ค้า/Drop (Customer Management)
      {
        code: "05",
        key: ERouteKey.MEMBERS_OUTLET,
        name: "ลูกค้า/ผู้ค้า/Drop",
        path: "customer",
        icon: <PersonPinTwoToneIcon />,
        element: <Outlet />,
        children: [
          {
            code: "05-04",
            key: ERouteKey.BUYER_LIST,
            name: "สาขาออเดอร์",
            path: "orders-branch",
            element: <OrdersBranch />,
          },
          {
            code: "05-01",
            key: ERouteKey.BUYER_LIST,
            name: "รายการลูกค้า",
            path: "customer-list",
            element: <UserBranchManagementPage />,
          },
          {
            code: "05-02",
            key: ERouteKey.BUYER_LIST,
            name: "จัดการจุดส่งลูกค้า",
            path: "customer-drop",
            element: <CustomerDropManagementPage />,
          },
          {
            key: ERouteKey.CUSTOMER_MANAGEMENT,
            name: "จัดการข้อมูลลูกค้า/ผู้ค้า",
            path: "customer-management",
            element: <CustomerManagementPage />,
          },
          {
            code: "05-03",
            key: ERouteKey.FRANCHISE_MANAGEMENT,
            name: "จัดการแฟรนไชส์",
            path: "franchise",
            icon: <StorefrontIcon />,
            element: <FranchisePage />,
          },
          {
            code: "05-05",
            key: ERouteKey.LINE_CHAT_HISTORY,
            name: "ไลน์แชท",
            path: "line-chat-history",
            element: <LineChatHistoryPage />,
          },
          {
            code: "05-06",
            key: ERouteKey.BROADCAST,
            name: "ไลน์บรอดแคสต์",
            path: "broadcast",
            element: <BroadcastLayout />,
            children: [
              {
                key: ERouteKey.BROADCAST_CENTER,
                path: "",
                element: <BroadcastPage />,
                isPrivateLink: true,
              },
              {
                key: ERouteKey.ORDER_REMINDER,
                path: "order-reminder",
                element: <OrderReminderPage />,
                isPrivateLink: true,
              },
            ],
          },
          {
            code: "05-07",
            key: ERouteKey.SUPPORT_TICKET_HELPDESK,
            name: "Helpdesk",
            path: "support-ticket-helpdesk",
            isPrivateLink: true,
            element: <Navigate to="/request-center" replace />,
          },
          {
            code: "05-08",
            key: ERouteKey.SMS_OTP_LOGS_LEGACY_REDIRECT,
            path: "sms-otp-logs",
            isPrivateLink: true,
            element: <Navigate to="/setting/sms-otp-logs" replace />,
          },
          // {
          //   // code: "05-05",
          //   key: ERouteKey.PROMOTION_MANAGEMENT,
          //   name: "โปรโมชั่น/คูปอง",
          //   path: "promotions",
          //   icon: <LoyaltyTwoToneIcon />,
          //   element: <PromotionsPage />,
          // },
        ],
      },

      // 6. ข้อมูลเว็บไซต์ (Web Data)
      // {
      //   code: "06",
      //   key: ERouteKey.WEBDATA_OUTLET,
      //   name: "Punthai Report",
      //   path: "punthai",
      //   icon: <LanguageIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       code: "06-01",
      //       key: ERouteKey.BANNER_HOMEPAGE_LIST_OUTLET,
      //       name: "Banner Homepage",
      //       path: "banner-homepage-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST,
      //           path: "",
      //           element: <BannerHomepageList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerHomepageListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerHomepageListEdit />,
      //         },
      //       ],
      //     },
      //     {
      //       code: "06-02",
      //       key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //       name: "Banner Greeting Modal",
      //       path: "banner-greeting-modal-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //           path: "",
      //           element: <BannerGreetingModalList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerGreetingModalListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerGreetingModalListEdit />,
      //         },
      //       ],
      //     },
      //   ],
      // },

      // 6. รายงานพันธุ์ไทย (Punthai Report)
      // {
      //   code: "06",
      //   key: ERouteKey.PUNTHAI,
      //   name: "รายงานพันธุ์ไทย",
      //   path: "punthai",
      //   icon: <DescriptionTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       code: "06-01",
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "รายงานพันธุ์ไทย",
      //       path: "punthai-report",
      //       element: <PunthaiReport />,
      //     },
      //     {
      //       code: "06-02",
      //       isPrivateLink: true,
      //       key: ERouteKey.REPORT_PUNTHAI_DETAIL,
      //       name: "รายงานพันธุ์ไทยรายละเอียด",
      //       path: "punthai-detail-report",
      //       element: <PunthaiDetailReport />,
      //     },
      //   ],
      // },

      // System Pages (Hidden)
      {
        code: "98",
        isPrivateLink: true,
        key: ERouteKey.DASHBOARD,
        name: "Dashboard",
        path: "dashboard",
        icon: <DashboardOutlinedIcon />,
        element: <DashBoardPage />,
      },

      // Franchise Management
      // {
      //   // code: "97",
      //   key: ERouteKey.FRANCHISE_MANAGEMENT,
      //   name: "จัดการแฟรนไชส์",
      //   path: "franchise",
      //   icon: <StorefrontIcon />,
      //   element: <FranchisePage />,
      // },

      // Customer Drop Management
      // {
      //   // code: "96",
      //   key: ERouteKey.CUSTOMER_DROP_MANAGEMENT,
      //   name: "จุดส่งลูกค้า",
      //   path: "customer-drop",
      //   icon: <LocalShippingIcon />,
      //   element: <CustomerDropManagementPage />,
      // },
    ],
  },
  {
    code: "90",
    key: ERouteKey.SETTING_OUTLET,
    name: "ตั้งค่า", // Renamed from "ตั้งค่าระบบ" to match diagram
    path: "setting",
    icon: <SettingsTwoToneIcon />,
    element: (
      <RequireAuth>
        <SettingLayout>
          <Suspense
            fallback={
              <div
                style={{
                  display: "grid",
                  width: "100%",
                  height: "100%",
                  placeItems: "center",
                }}
              >
                <CircularProgress color="primary" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </SettingLayout>
      </RequireAuth>
    ),
    children: [
      {
        key: ERouteKey.SETTING_OUTLET_NAVIGATE,
        path: "",
        element: <Navigate to="business" />,
      },

      // NEW SETTINGS STRUCTURE BASED ON DIAGRAM
      {
        code: "05-08",
        key: ERouteKey.SMS_OTP_LOGS,
        name: "SMS/OTP Logs",
        path: "sms-otp-logs",
        icon: <SmsOutlinedIcon />,
        element: <SmsOtpLogsPage />,
      },
      {
        code: "03-07",
        key: ERouteKey.SETTING_DELIVERY_INTEGRATION,
        name: "เชื่อมต่อ Delivery",
        sidebarName: "เชื่อมต่อ Delivery",
        path: "delivery-integration",
        icon: <LocalShippingIcon />,
        element: <DeliveryIntegrationSettingsPage />,
      },
      {
        code: "03-07",
        key: ERouteKey.OSRM_ROUTE_SYNC_REVIEW,
        name: "ตรวจข้อมูลสายส่ง",
        sidebarName: "ตรวจข้อมูลสายส่ง",
        path: "osrm-route-sync-review",
        icon: <SyncAltIcon />,
        element: <OsrmRouteSyncReviewPage />,
      },
      {
        code: "03-03",
        key: ERouteKey.LINE_TRANSPORT_MANAGEMENT,
        name: "ห้องขนส่ง LINE",
        sidebarName: "ห้องขนส่ง LINE",
        path: "delivery-transport",
        icon: <LocalShippingIcon />,
        element: <LineTransportManagementPage />,
      },
      {
        code: "91",
        key: ERouteKey.SETTING_BUSINESS,
        name: "ข้อมูลกิจการ", // Renamed from "ข้อมูลกิจการ" to match diagram
        path: "business",
        icon: <BusinessTwoToneIcon />,
        element: <SettingBusinessPage />,
      },
      // {
      //   // code: "91-01",
      //   key: ERouteKey.SETTING_AI,
      //   name: "ตั้งค่า AI ส่วนกลาง",
      //   path: "ai",
      //   icon: <AutoAwesomeIcon />,
      //   element: <BroadcastAiSettingPage />,
      // },
      // {
      //   key: ERouteKey.SETTING_EPOD_GEOFENCE,
      //   name: "EPOD Geofence",
      //   path: "epod-geofence",
      //   icon: <PinDropOutlinedIcon />,
      //   element: <EpodGeofenceSettingPage />,
      // },
      {
        key: ERouteKey.SETTING_APP_OPERATIONS,
        name: "สถานะผู้ใช้งาน",
        path: "app-operations",
        icon: <LocalShippingIcon />,
        element: <Outlet />,
        children: [
          // {
          //   code: "03-07",
          //   isPrivateLink: true,
          //   key: ERouteKey.SETTING_APP_OPERATIONS_DEFAULT,
          //   path: "",
          //   element: <Navigate to="epod" replace />,
          // },
          {
            // code: "03-07",
            key: ERouteKey.EPOD_APP_OPERATIONS,
            name: "ผู้ใช้งานแอป",
            path: "epod",
            icon: <LocalShippingIcon />,
            element: <EpodAppOperationsPage />,
          },
          {
            // code: "90",
            key: ERouteKey.SETTING_SESSION_MANAGEMENT,
            name: "ผู้ใช้งานเว็บ",
            path: "sessions",
            icon: <SecurityOutlinedIcon />,
            element: <SessionManagementPage />,
          },
        ],
      },
      // {
      //   code: "03-07",
      //   isPrivateLink: true,
      //   key: ERouteKey.SETTING_EPOD_APP_OPERATIONS_LEGACY_REDIRECT,
      //   path: "epod-app-operations",
      //   element: <Navigate to="/setting/app-operations/epod" replace />,
      // },
      // {
      //   code: "90",
      //   isPrivateLink: true,
      //   key: ERouteKey.SETTING_SESSION_MANAGEMENT_LEGACY_REDIRECT,
      //   path: "session-management",
      //   element: <Navigate to="/setting/app-operations/sessions" replace />,
      // },
      {
        code: "92",
        key: ERouteKey.OUTLET,
        name: "หมวดหมู่", // New menu based on diagram
        path: "setting-categories",
        icon: <CategoryIcon />,
        element: (
          <UnderConstruction
            title="จัดการหมวดหมู่"
            description="ระบบจัดการหมวดหมู่สินค้ากำลังอยู่ในระหว่างการพัฒนา"
          />
        ),
        // children: [
        // {
        //   key: ERouteKey.OUTLET,
        //   path: "",
        //   element: <Navigate to="setup" replace />,
        // },
        // {
        //   code: "92-01",
        //   key: ERouteKey.SETUP_CATEGORIES,
        //   name: "กำหนดหมวดหมู่",
        //   path: "setup-categories",
        //   element: <ProductCategoriesSetup />,
        // },
        // {
        //   code: "92-02",
        //   key: ERouteKey.SETUP_SHORTCUTS,
        //   name: "ทางลัดหมวดหมู่",
        //   path: "shortcuts",
        //   element: <ProductCategoriesShortcuts />,
        // },
        // ],
      },
      // {
      //   code: "93",
      //   key: ERouteKey.SETTING_PRODUCT_SHIPPING,
      //   name: "ข้อมูลขนส่ง", // New menu based on diagram
      //   path: "shipping-info",
      //   icon: <LocalShippingIcon />,
      //   element: (
      //     <UnderConstruction
      //       title="ข้อมูลขนส่ง"
      //       description="ระบบจัดการข้อมูลขนส่งกำลังอยู่ในระหว่างการพัฒนา"
      //     />
      //   ),
      // },
      {
        code: "94",
        key: ERouteKey.SETTING_PRODUCT_SHIPPING,
        name: "วันจัดส่ง", // New menu based on diagram
        path: "delivery-schedule",
        icon: <LocalShippingIcon />,
        element: (
          <UnderConstruction
            title="วันจัดส่ง"
            description="ระบบจัดการวันจัดส่งกำลังอยู่ในระหว่างการพัฒนา"
          />
        ),
      },

      // KEEP ACCESS CONTROL AND USER MANAGEMENT
      {
        code: "95",
        key: ERouteKey.SETTING_ACCESS,
        name: "สิทธิ์การใช้งาน",
        path: "access",
        icon: <VerifiedUserTwoToneIcon />,
        element: <Outlet />,
        children: [
          {
            key: ERouteKey.SETTING_ACCESS_DEFAULT,
            path: "",
            element: <Navigate to="user" />,
          },
          {
            code: "95-01",
            key: ERouteKey.SETTING_ACCESS_USER,
            name: "ผู้ใช้งาน",
            path: "user",
            element: <SettingAccessUserPage />,
          },
          {
            code: "95-02",
            key: ERouteKey.SETTING_ACCESS_USER_GROUP,
            name: "กลุ่มผู้ใช้งาน",
            path: "userGroup",
            element: <SettingAccessUserGroupPage />,
          },
        ],
      },

      // User-Branch Management
      {
        code: "96",
        key: ERouteKey.USER_BRANCH_MANAGEMENT,
        name: "จัดการ User-Branch",
        path: "user-branch",
        icon: <FolderSharedOutlinedIcon />,
        element: <UserBranchManagementPage />,
      },

      // Billing Cycle Template Management
      {
        // code: "98",
        key: ERouteKey.SETTING_BILLING_CYCLE_TEMPLATE,
        name: "รูปแบบรอบบิล",
        path: "billing-cycle-template",
        icon: <CalendarMonthOutlinedIcon />,
        element: <BillingCycleTemplatePage />,
      },

      // Environment Badge Demo (Development only)
      {
        code: "97",
        key: ERouteKey.SETTING_ENVIRONMENT_BADGE_DEMO,
        name: "Environment Badge Demo",
        path: "environment-badge-demo",
        icon: <BugReportIcon />,
        element: <EnvironmentBadgeDemoPage />,
        isPrivateLink: import.meta.env.VITE_IS_DEV !== "true", // Hide in production
      },

      // Super Admin - Hidden Menu (Access Control Required)
      // {
      //   code: "97",
      //   isPrivateLink: true, // Hide from menu
      //   key: ERouteKey.SUPER_ADMIN_DATA_MANAGEMENT,
      //   name: "🔒 Super Admin Control Panel",
      //   path: "super-admin",
      //   icon: <AdminPanelSettingsIcon />,
      //   element: (
      //     <SuperAdminPasswordGuard>
      //       <SuperAdminDashboard />
      //     </SuperAdminPasswordGuard>
      //   ),
      // },

      // TODO: Comment out old menu structure - will be restored later if needed
      // OLD MENU STRUCTURE - COMMENTED OUT
      // {
      //   // code: "06",
      //   key: ERouteKey.REPORT_PUNTHAI,
      //   name: "รายงาน",
      //   path: "punthai",
      //   icon: <DescriptionTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       code: "06-01",
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "รายงานพันธุ์ไทย",
      //       path: "punthai-report",
      //       element: <PunthaiReport />,
      //     },
      //     {
      //       code: "04-01",
      //       key: ERouteKey.REPORT_BUYER,
      //       name: "รายงานการส่งสินค้า",
      //       path: "delivery-report",
      //       // TODO: Replace with proper component when Purchasing module is fixed
      //       element: <DeliveryReport />,
      //     },
      //     {
      //       code: "06-02",
      //       isPrivateLink: true,
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "รายงานพันธุ์ไทย",
      //       path: "punthai-detail-report",
      //       element: <PunthaiDetailReport />,
      //     },
      //   ],
      // },
      // {
      //   // code: "08",
      //   key: ERouteKey.PRODUCT_OUTLET,
      //   name: "รายการขาย",
      //   path: "product",
      //   icon: <PointOfSaleTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       // code: "08-03",
      //       key: ERouteKey.ORDERS_LINE,
      //       name: "รายการขาย",
      //       path: "orders-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           isPrivateLink: true,
      //           key: ERouteKey.PRODUCT_LIST_LINE,
      //           name: "รายการสั่งซื้อในระบบ Line",
      //           path: "",
      //           element: <LineShopOrders />,
      //         },
      //       ],
      //     },
      //     // {
      //     //   // code: "08-03",
      //     //   key: ERouteKey.ORDERS_LINE_RETURN,
      //     //   name: "รายการคืนสินค้า",
      //     //   path: "orders-list-return",
      //     //   element: <Outlet />,
      //     //   children: [
      //     //     {
      //     //       isPrivateLink: true,
      //     //       key: ERouteKey.PRODUCT_LIST_LINE,
      //     //       name: "รายการสั่งซื้อในระบบ Line",
      //     //       path: "",
      //     //       element: <LineShopOrders />,
      //     //     },
      //     //   ],
      //     // },
      //   ],
      // },
      // {
      //   // code: "00",
      //   isPrivateLink: true,
      //   key: ERouteKey.DELIVERY,
      //   name: "รายการจัดส่ง",
      //   path: "delivery",
      //   icon: <LocalShippingTwoToneIcon />,
      //   element: <DeliverySchedule />,
      //   children: [
      //     {
      //       // code: "04-01",
      //       key: ERouteKey.REPORT_BUYER,
      //       name: "รายการส่งสินค้า",
      //       path: "delivery-report",
      //       element: <DeliveryReport />,
      //     },
      //     {
      //       // code: "04-02",
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "จัดสายส่ง",
      //       path: "punthai-report",
      //       element: <DeliveryReport />,
      //     },
      //     {
      //       // code: "04-02",
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "ขนส่ง",
      //       path: "deliver",
      //       element: <DeliveryReport />,
      //     },
      //     {
      //       // code: "04-02",
      //       key: ERouteKey.REPORT_PUNTHAI,
      //       name: "Driver",
      //       path: "driver",
      //       element: <DeliveryReport />,
      //     },
      //     // {
      //     //     code: "04-01",
      //     //     key: ERouteKey.REPORT_BUYER,
      //     //     name: 'รายงานการส่งสินค้า',
      //     //     path: 'punthai/delivery-report',
      //     //     element: <DeliveryReport />
      //     // },
      //   ],
      // },
      // {
      //   // code: "08",
      //   key: ERouteKey.PRODUCT_OUTLET,
      //   name: "SKU",
      //   path: "product",
      //   icon: <LoyaltyTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     // {
      //     //     code: "08-01",
      //     //     key: ERouteKey.PRODUCT_LIST,
      //     //     name: "รายการสินค้าในระบบ",
      //     //     path: "product-list",
      //     //     element: <ProductList />,
      //     // },
      //     {
      //       // code: "08-02",
      //       key: ERouteKey.OUTLET,
      //       name: "รายการ SKU",
      //       path: "product-list-line",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           isPrivateLink: true,
      //           key: ERouteKey.PRODUCT_LIST_LINE,
      //           name: "สร้างสินค้าในระบบ Line",
      //           path: "",
      //           element: <ProductListLine />,
      //         },
      //         {
      //           isPrivateLink: true,
      //           key: ERouteKey.PRODUCT_LIST_LINE_CREATE,
      //           name: "สร้างสินค้าในระบบ Line",
      //           path: "create",
      //           element: <ProductListLineCreate />,
      //         },
      //         {
      //           isPrivateLink: true,
      //           key: ERouteKey.OUTLET,
      //           name: "แก้ไขสินค้าในระบบ Line OUTLET",
      //           path: "edit",
      //           element: <ProductListLineEdit />,
      //           children: [
      //             {
      //               isPrivateLink: true,
      //               key: ERouteKey.PRODUCT_LIST_LINE_EDIT,
      //               name: "แก้ไขสินค้าในระบบ Line",
      //               path: ":id",
      //               element: <ProductListLineEdit />,
      //             },
      //           ],
      //         },
      //       ],
      //     },
      //     {
      //       // code: "08-03",
      //       key: ERouteKey.ORDERS_LINE,
      //       name: "หมวดหมู่",
      //       path: "orders-list",
      //       element: <Outlet />,
      //     },
      //     // TODO: Temporarily commented out WareHouse menu due to module import issues
      //     // {
      //     //   // code: "04-02",
      //     //   key: ERouteKey.WAREHOUSE_INFORMATION_OUTLET,
      //     //   name: "คลังสินค้า",
      //     //   path: "information",
      //     //   element: <Outlet />,
      //     //   children: [
      //     //     {
      //     //       key: ERouteKey.WAREHOUSE_INFORMATION,
      //     //       path: "",
      //     //       element: <WareHouseInformationPage />,
      //     //     },
      //     //     {
      //     //       key: ERouteKey.WAREHOUSE_INFORMATION_CREATE,
      //     //       name: "เพิ่มคลังสินค้า",
      //     //       path: "create",
      //     //       element: <WareHouseInformationCreatePage />,
      //     //     },
      //     //     {
      //     //       key: ERouteKey.WAREHOUSE_INFORMATION_BY_UUID,
      //     //       name: "ข้อมูลคลังสินค้า",
      //     //       path: ":uuid",
      //     //       element: <WareHouseInformationCreatePage />,
      //     //     },
      //     //   ],
      //     // },
      //     // {
      //     //   // code: "08-03",
      //     //   key: ERouteKey.ORDERS_LINE,
      //     //   name: "รายการโอนสินค้า",
      //     //   path: "orders-list-transfer",
      //     //   element: <Outlet />,
      //     // },
      //     // {
      //     //     code: "08-03",
      //     //     key: ERouteKey.ORDERS_LINE,
      //     //     name: "รายการสั่งซื้อ",
      //     //     path: "orders-list",
      //     //     element: <Outlet />,
      //     //     children: [
      //     //         {
      //     //             isPrivateLink: true,
      //     //             key: ERouteKey.PRODUCT_LIST_LINE,
      //     //             name: "รายการสั่งซื้อในระบบ Line",
      //     //             path: "",
      //     //             element: <LineShopOrders />,
      //     //         },
      //     //     ],
      //     // },
      //   ],
      // },
      // Hidden/System Pages

      // {
      //   code: "99",
      //   key: ERouteKey.DASHBOARD,
      //   name: "Dashboard",
      //   path: "dashboard",
      //   icon: <DashboardOutlinedIcon />,
      //   element: <DashBoardPage />,
      // },

      // 6. ข้อมูลเว็บไซต์ (Web Data)
      // {
      //   code: "06",
      //   key: ERouteKey.WEBDATA_OUTLET,
      //   name: "ข้อมูลเว็บไซต์",
      //   path: "webdata",
      //   icon: <LanguageIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       code: "06-01",
      //       key: ERouteKey.BANNER_HOMEPAGE_LIST_OUTLET,
      //       name: "Banner Homepage",
      //       path: "banner-homepage-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST,
      //           path: "",
      //           element: <BannerHomepageList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerHomepageListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerHomepageListEdit />,
      //         },
      //       ],
      //     },
      //     {
      //       code: "06-02",
      //       key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //       name: "Banner Greeting Modal",
      //       path: "banner-greeting-modal-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //           path: "",
      //           element: <BannerGreetingModalList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerGreetingModalListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerGreetingModalListEdit />,
      //         },
      //       ],
      //     },
      //   ],
      // },

      // TODO: Comment out old individual menu items - will be organized under new structure
      // OLD INDIVIDUAL ITEMS - COMMENTED OUT
      // {
      //   code: "00",
      //   isPrivateLink: true,
      //   key: ERouteKey.WELCOME,
      //   name: "Welcome",
      //   path: "welcome",
      //   icon: <DashboardOutlinedIcon />,
      //   element: <WelcomePage />,
      // },
      // {
      //   code: "01",
      //   key: ERouteKey.DASHBOARD,
      //   name: "Dashboard",
      //   path: "dashboard",
      //   icon: <DashboardOutlinedIcon />,
      //   element: <DashBoardPage />,
      // },
      // TODO: Comment out old unused menu sections
      // OLD PURCHASING MODULE - COMMENTED OUT
      // TODO: Temporarily commented out entire Purchasing menu due to module import issues
      // {
      //     // code: "03",
      //     key: ERouteKey.PURCHASE_OUTLET,
      //     name: "แผนการจัดซื้อ",
      //     path: "purchasing",
      //     icon: <TodayTwoToneIcon />,
      //     element: <Outlet />,
      //     children: [
      //         {
      //             // code: "03-01",
      //             key: ERouteKey.PURCHASE_LIST_OUTLET,
      //             name: "รายสินค้า",
      //             path: "item",
      //             element: <Outlet />,
      //             children: [
      //                 {
      //                     key: ERouteKey.PURCHASE_LIST,
      //                     path: "",
      //                     element: <PurchasingItemPage />,
      //                 },
      //                 {
      //                     name: "ข้อมูลรายการสินค้า",
      //                     key: ERouteKey.PURCHASE_LIST_DETAIL_OUTLET,
      //                     path: "detail",
      //                     element: <Outlet />,
      //                     children: [
      //                         {
      //                             key: ERouteKey.PURCHASE_LIST_DETAIL_OUTLET_NAVIGATE,
      //                             path: "",
      //                             element: <Navigate to={"/purchasing/item"} />,
      //                         },
      //                         {
      //                             isPrivateLink: true,
      //                             name: "รายละเอียด",
      //                             key: ERouteKey.PURCHASE_LIST_DETAIL_ID,
      //                             path: ":id",
      //                             element: <PurchasingItemDetailPage />,
      //                         },
      //                     ],
      //                 },
      //             ],
      //         },
      //         {
      //             // code: "03-02",
      //             key: ERouteKey.PURCHASE_ORDERS_OUTLET,
      //             name: "ใบสั่งผลิต",
      //             path: "productionOrder",
      //             element: <Outlet />,
      //             children: [
      //                 {
      //                     key: ERouteKey.PURCHASE_ORDERS,
      //                     path: "",
      //                     element: <PurchasingProductionOrderPage />,
      //                 },
      //                 {
      //                     isPrivateLink: true,
      //                     key: ERouteKey.PURCHASE_ORDERS_COPY,
      //                     name: "ใบสั่งผลิต",
      //                     path: "copy",
      //                     // element: <CopyPurchasingProductionOrderDetailPage />,
      //                     element: <PurchasingProductionOrderDetailPage />,
      //                 },
      //                 {
      //                     name: "ข้อมูลใบสั่งผลิต",
      //                     key: ERouteKey.PURCHASE_ORDERS_DETAIL_OUTLET,
      //                     path: "detail",
      //                     element: <Outlet />,
      //                     children: [
      //                         {
      //                             key: ERouteKey.PURCHASE_ORDERS_DETAIL_OUTLET_NAVIGATE,
      //                             path: "",
      //                             element: <PurchasingProductionOrderDetailPage />,
      //                         },
      //                         {
      //                             isPrivateLink: true,
      //                             key: ERouteKey.PURCHASE_LIST_DETAIL_ID,
      //                             path: ":id",
      //                             element: <PurchasingProductionOrderDetailPage />,
      //                         },
      //                     ],
      //                 },
      //             ],
      //         },
      //         {
      //             // code: "03-03",
      //             key: ERouteKey.PURCHASE_BUYING_OUTLET,
      //             name: "ใบสั่งซื้อ",
      //             path: "purchaseOrder",
      //             element: <Outlet />,
      //             children: [
      //                 {
      //                     key: ERouteKey.PURCHASE_BUYING,
      //                     path: "",
      //                     element: <PurchasingPurchaseOrderPage />,
      //                 },
      //                 {
      //                     name: "ข้อมูลใบสั่งผลิต",
      //                     key: ERouteKey.PURCHASE_BUYING_DETAIL_OUTLET,
      //                     path: "detail",
      //                     element: <Outlet />,
      //                     children: [
      //                         {
      //                             key: ERouteKey.PURCHASE_BUYING_OUTLET_NAVIGATE,
      //                             path: "",
      //                             element: <PurchasingPurchaseOrderDetailPage />,
      //                         },
      //                         {
      //                             isPrivateLink: true,
      //                             key: ERouteKey.PURCHASE_BUYING_DETAIL_ID,
      //                             path: ":id",
      //                             element: <PurchasingPurchaseOrderDetailPage />,
      //                         },
      //                     ],
      //                 },
      //             ],
      //         },
      //     ],
      // },

      // OLD MEMBERS STRUCTURE - COMMENTED OUT
      // {
      //   // code: "02",
      //   key: ERouteKey.MEMBERS_OUTLET,
      //   name: "ลูกค้า/จุด ส่ง-รับ",
      //   path: "members",
      //   icon: <PersonPinTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     // {
      //     //     key: ERouteKey.MEMBERS_OUTLET,
      //     //     path: "",
      //     //     element: <Navigate to="/dashboard" />,
      //     // },
      //     // {
      //     //     key: ERouteKey.MEMBER_REGISTER,
      //     //     name: "Register",
      //     //     path: "register",
      //     //     element: <Register />,
      //     // },
      //     // {
      //     //     key: ERouteKey.MEMBER_SUBSCRIBE,
      //     //     name: "Subscribe",
      //     //     path: "subscribe",
      //     //     element: <Subscribe />,
      //     // },
      //     {
      //       key: ERouteKey.BUYER_LIST,
      //       name: "ผู้ติดต่อ",
      //       path: "buyer",
      //       element: <Buyer />,
      //     },
      //     // {
      //     //   key: ERouteKey.BUYER_LIST,
      //     //   name: "จุดส่ง/รับสินค้า",
      //     //   path: "buyer",
      //     //   element: <Buyer />,
      //     // },
      //     // {
      //     //     key: ERouteKey.MEMBER_SELLER,
      //     //     name: "Seller",
      //     //     path: "seller",
      //     //     element: <Seller />,
      //     // },
      //     // {
      //     //     key: ERouteKey.MEMBER_NEWS_LETTER,
      //     //     name: 'News letter ',
      //     //     path: 'news-letter',
      //     //     element: <NewsLetter />
      //     // },
      //   ],
      // },

      // OLD FINANCE MODULE - COMMENTED OUT
      // {
      //     code: "03",
      //     key: ERouteKey.FINANCE_OUTLET,
      //     name: "การเงิน",
      //     path: "finance",
      //     icon: <PaidOutlinedIcon />,
      //     element: <Outlet />,
      //     children: [
      //         {
      //             key: ERouteKey.FINANCE_OUTLET,
      //             path: "",
      //             element: <Navigate to="/dashboard" />,
      //         },
      //         {
      //             key: ERouteKey.FINANCE_BUY_SELL,
      //             name: "ข้อมูล ซื้อ/ขาย",
      //             path: "buy-sell",
      //             element: <BuySell />,
      //         },
      //         {
      //             key: ERouteKey.FINANCE_FINANCE_INFO,
      //             name: "ข้อมูลการเงิน",
      //             path: "finance-info",
      //             element: <FinanceInfo />,
      //         },
      //     ],
      // },

      // OLD HELPDESK MODULE - COMMENTED OUT
      // {
      //     code: "05",
      //     key: ERouteKey.HELPDESK_SUPPORT_OUTLET,
      //     name: 'Helpdesk Support',
      //     path: 'helpdesk-support',
      //     icon: <HelpOutlineOutlinedIcon />,
      //     element: <Outlet />,
      //     children: [
      //         {
      //             key: ERouteKey.HELPDESK_SUPPORT_OUTLET,
      //             path: '',
      //             element: <Navigate to="/dashboard" />
      //         },
      //         {
      //             code: "01",
      //             key: ERouteKey.HELPDESK_SUPPORT_COMPLAINTS,
      //             name: 'ข้อร้องเรียน',
      //             path: 'complaints',
      //             element: <Complaints />
      //         },
      //         {
      //             code: "01",
      //             key: ERouteKey.HELPDESK_SUPPORT_HELOPDESK_SETTING,
      //             name: 'Helpdesk Setting',
      //             path: 'helpdesk-setting',
      //             element: <HelpdeskSetting />
      //         },
      //     ]
      // },

      // OLD WEBDATA MODULE - COMMENTED OUT
      // {
      //   code: "80",
      //   key: ERouteKey.WEBDATA_OUTLET,
      //   name: "ข้อมูลเว็บไซต์",
      //   path: "webdata",
      //   icon: <LanguageIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       code: "80-01",
      //       key: ERouteKey.BANNER_HOMEPAGE_LIST_OUTLET,
      //       name: "Banner Homepage",
      //       path: "banner-homepage-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST,
      //           path: "",
      //           element: <BannerHomepageList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerHomepageListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_HOMEPAGE_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerHomepageListEdit />,
      //         },
      //       ],
      //     },
      //     {
      //       code: "80-02",
      //       key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //       name: "Banner Greeting Modal",
      //       path: "banner-greeting-modal-list",
      //       element: <Outlet />,
      //       children: [
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST,
      //           path: "",
      //           element: <BannerGreetingModalList />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_CREATE,
      //           name: "Banner Create",
      //           path: "create",
      //           element: <BannerGreetingModalListCreate />,
      //         },
      //         {
      //           key: ERouteKey.BANNER_GREETING_MODAL_LIST_EDIT,
      //           name: "Banner Edit",
      //           path: "edit/:id",
      //           element: <BannerGreetingModalListEdit />,
      //         },
      //       ],
      //     },
      //   ],
      // },

      // {
      //   code: "96",
      //   key: ERouteKey.USER_BRANCH_MANAGEMENT,
      //   name: "จัดการ User-Branch",
      //   path: "user-branch",
      //   icon: <FolderSharedOutlinedIcon />,
      //   element: <UserBranchManagementPage />,
      // },

      // TODO: Comment out old settings structure
      // OLD SETTINGS STRUCTURE - COMMENTED OUT
      // {
      //   code: "91",
      //   key: ERouteKey.SETTING_BUSINESS,
      //   name: "ข้อมูลกิจการ",
      //   path: "business",
      //   icon: <BusinessTwoToneIcon />,
      //   element: <SettingBusinessPage />,
      // },
      // {
      //   // code: "92",
      //   key: ERouteKey.SETTING_ACCESS,
      //   name: "สิทธิ์การใช้งาน",
      //   path: "access",
      //   icon: <VerifiedUserTwoToneIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       key: ERouteKey.SETTING_ACCESS_DEFAULT,
      //       path: "",
      //       element: <Navigate to="user" />,
      //     },
      //     {
      //       // code: "92-01",
      //       key: ERouteKey.SETTING_ACCESS_USER,
      //       name: "ผู้ใช้งาน",
      //       path: "user",
      //       element: <SettingAccessUserPage />,
      //     },
      //     {
      //       // code: "92-02",
      //       key: ERouteKey.SETTING_ACCESS_USER_GROUP,
      //       name: "กลุ่มผู้ใช้งาน",
      //       path: "userGroup",
      //       element: <SettingAccessUserGroupPage />,
      //     },
      //   ],
      // },
      // {
      //   code: "93",
      //   key: ERouteKey.OUTLET,
      //   name: "ตั้งค่าหมวดหมู่สินค้า",
      //   path: "product-categories",
      //   icon: <CategoryIcon />,
      //   element: <Outlet />,
      //   children: [
      //     {
      //       key: ERouteKey.OUTLET,
      //       path: "",
      //       element: <Navigate to="/dashboard" replace />,
      //     },
      //     {
      //       code: "93-01",
      //       key: ERouteKey.SETUP_CATEGORIES,
      //       name: "กำหนดหมวดหมู่",
      //       path: "setup",
      //       element: <ProductCategoriesSetup />,
      //     },
      //     {
      //       code: "93-02",
      //       key: ERouteKey.SETUP_SHORTCUTS,
      //       name: "ทางลัดหมวดหมู่",
      //       path: "shortcuts",
      //       element: <ProductCategoriesShortcuts />,
      //     },
      //   ],
      // },
      // {
      //   code: "96",
      //   key: ERouteKey.SETTING_PRODUCT_SHIPPING,
      //   name: "ผู้ให้บริการขนส่ง",
      //   path: "shipping",
      //   icon: <LocalShippingIcon />,
      //   element: <ShippingProviders />,
      // },
      // {
      //   code: "97",
      //   key: ERouteKey.SETTING_PRODUCT_SHIPPING,
      //   name: "ตั้งค่ารอบส่ง",
      //   path: "delivery-schedule",
      //   icon: <LocalShippingIcon />,
      //   element: <ShippingProviders />,
      // },
      // {
      //   code: "98",
      //   key: ERouteKey.SETTING_PRODUCT_SHIPPING,
      //   name: "ตั้งค่าโซน",
      //   path: "branch-zone",
      //   icon: <LocalShippingIcon />,
      //   element: <ShippingProviders />,
      // },
      // {
      //   code: "99",
      //   key: ERouteKey.USER_BRANCH_MANAGEMENT,
      //   name: "จัดการ User-Branch",
      //   path: "user-branch",
      //   icon: <FolderSharedOutlinedIcon />,
      //   element: <UserBranchManagementPage />,
      // },
    ],
  },
  {
    key: ERouteKey.LOGIN,
    name: "เข้าสู่ระบบ",
    path: "login",
    element: (
      <NoAuth>
        <LoginPage />
      </NoAuth>
    ),
  },
  {
    key: 'MILKRUNN_BOT_LINK',
    name: 'เชื่อมบัญชี MilkRunnBot',
    path: 'milkrunn-bot/link',
    isPrivateLink: true,
    element: (
      <Suspense fallback={<CircularProgress />}>
        <MilkrunnBotLinkPage />
      </Suspense>
    ),
  },
  {
    key: ERouteKey.PRIVACY,
    name: "นโยบายความเป็นส่วนตัว",
    path: "privacy",
    code: "",
    element: (
      <Suspense fallback={<CircularProgress color="primary" />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },
  {
    key: ERouteKey.FARMBOOK_LOGIN,
    name: "เข้าสู่ระบบด้วย farmbook",
    path: "farmBookLogin",
    element: (
      <NoAuth>
        <FarmBookLoginPage />
      </NoAuth>
    ),
  },
  // {
  //     key: ERouteKey.SITEMAP,
  //     name: "หน้าทั้งหมด",
  //     path: "sitemap",
  //     element: <SiteMap />,
  // },
  {
    key: ERouteKey.NOT_FOUND,
    name: "ไม่พบปลายทาง",
    path: "*",
    element: <NotFoundPage />,
  },
  {
    key: ERouteKey.NO_PERMISSION,
    name: "ไม่มีสิทธิ์เข้าถึง",
    path: "no-permission",
    element: <NoPermissionPage />,
  },
];
